import React, { FC, useCallback, useEffect, useState } from 'react'

import { ReportRequestConfirmationBottomSheet } from 'components/report-request-confirmation-bootomsheet/ReportRequestConfirmationBottomSheet'
import { useAppContext } from 'contexts/app-context/hooks/useAppContext'
import { useRequestReport } from 'pages/your-personal-data/hooks/useRequestReport'
import { useGetReportStatus } from 'pages/your-personal-data/hooks/useGetReportStatus'
import { InterSvgIcon } from 'components'
import { map } from 'lodash'
import { TReportCard } from 'contexts/app-context/types'
import { logBridgeError, sendSelectContent } from 'common/helpers'
import { Routes } from 'common/constants'
import { Button } from 'common/styles/layouts'

import { Text } from '@interco/inter-ui/components/Text'

import {
  ReportCardContainer,
  ReportCardContent,
  ReportCardContainerBulletPoints,
  ReportCardContainerTitle,
  ReportCardContainerTitleIcon,
} from './styles'
import { createRequestDateElement } from './createRequestDateElement'

type ReportCardProps = Exclude<TReportCard, 'isActive'>

export const ReportCard: FC<ReportCardProps> = ({
  type,
  icon,
  title,
  details,
  bulletPoints,
  enabledButton,
  disabledButton,
}) => {
  const { userLanguage, userSelectedEntity } = useAppContext()

  const { data, isLoading: isCheckingStatus } = useGetReportStatus(type)

  const {
    requestReport,
    isRequestedReportLoading,
    isRequestedReportError,
    isRequestedReportSuccess,
    reset,
  } = useRequestReport(type)

  const [showBottomSheet, setShowBottomSheet] = useState(false)

  useEffect(() => {
    if (isRequestedReportSuccess) setShowBottomSheet(false)
  }, [isRequestedReportSuccess, setShowBottomSheet])

  const onButtonClickHandler = useCallback(() => {
    reset()
    setShowBottomSheet(true)
  }, [reset])

  const isDisabled = data?.status !== 'LIBERADA' || isRequestedReportLoading

  const isLoading = isCheckingStatus || isRequestedReportLoading

  const onBottomsheetCancelClickHandler = useCallback(() => {
    setShowBottomSheet(false)
  }, [setShowBottomSheet])

  const onBottomsheetConfirmClickHandler = useCallback(async () => {
    try {
      await sendSelectContent({
        flow: 'report_card',
        step: 'your_personal_data',
        screen_name: Routes.YOUR_PERSONAL_DATA,
        element_name: 'confirmReportRequest',
        element_type: 'button',
        parent_name: 'ReportCard',
        scenario: JSON.stringify({
          screen: Routes.YOUR_PERSONAL_DATA,
          product: userSelectedEntity,
          type,
          ref_type: 'button',
          content_action: 'touch',
        }),
      })
    } catch (error: unknown) {
      logBridgeError('send report card confirm tic', error)
    }

    await requestReport()
  }, [requestReport, type, userSelectedEntity])

  return (
    <>
      <ReportCardContainer>
        <ReportCardContent>
          <ReportCardContainerTitle>
            <ReportCardContainerTitleIcon>
              <InterSvgIcon iconRef={icon} width={24} height={24} color="var(--typography500)" />
            </ReportCardContainerTitleIcon>
            <Text variant="body-3" color="typography" colorWeight={500} bold>
              {title}
            </Text>
          </ReportCardContainerTitle>
          <Text variant="body-3" color="typography">
            {details}
          </Text>
          {bulletPoints && (
            <ReportCardContainerBulletPoints>
              {map(bulletPoints, (bulletPoint) => (
                <li key={bulletPoint}>
                  <Text variant="body-3" color="typography" colorWeight={400} fontWeight={400}>
                    {bulletPoint}
                  </Text>
                </li>
              ))}
            </ReportCardContainerBulletPoints>
          )}
        </ReportCardContent>
        <Button
          size="small"
          isLoading={isLoading}
          disabled={isDisabled}
          onClick={onButtonClickHandler}
        >
          {isDisabled ? disabledButton : enabledButton}
        </Button>
        {data &&
          userLanguage &&
          createRequestDateElement({
            date: data.dataSolicitacao,
            status: data.status,
            language: userLanguage,
          })}
      </ReportCardContainer>
      {showBottomSheet && (
        <ReportRequestConfirmationBottomSheet
          onConfirm={onBottomsheetConfirmClickHandler}
          onCancel={onBottomsheetCancelClickHandler}
          isRequestLoading={isRequestedReportLoading}
          isRequestSuccess={isRequestedReportSuccess}
          isRequestError={isRequestedReportError}
          bulletPoints={bulletPoints}
        />
      )}
    </>
  )
}
